import type * as React from 'react';
import cx from 'classnames';
import { useBrandConfig } from '@dx-ui/global-ui-prism';
import Icon from '@dx-ui/osc-icon';

const iconMapper: { mapPin: 'map-pin'; location: 'location' } = {
  mapPin: 'map-pin',
  location: 'location',
};

export type CaptionIcon = 'mapPin' | 'location';

export type CaptionProps = {
  /**
   * Optional, provides the text content for the caption
   */
  caption?: string;
  /**
   * Optional link url
   */
  captionLink?: string;
  /**
   * Optional link target
   */
  captionLinkTarget?: string;
  /**
   * Optional metrics data
   */
  metricsOnClick?: () => void;
  /*
   * Optional alignment, default is end
   */
  align?: 'start' | 'end';
  /*
   * Optional placement, default is top
   */
  placement?: 'top' | 'bottom';
  /*
   * only 2 caption osc-icons allowed at the moment, map-pin and location
   */
  captionIcon?: CaptionIcon;
  anchorClassName?: string;
  /**
   * Optional callback when the caption link receives focus
   */
  onLinkFocus?: React.FocusEventHandler<HTMLAnchorElement>;
};

/**
 * Image caption is an optional prop that is consumed by other components, this cheese does not stand alone. Can be text only or a clickable link.
 */
export const Caption: React.FC<CaptionProps> = ({
  caption,
  captionLink,
  captionIcon,
  captionLinkTarget,
  align = 'end',
  placement = 'top',
  metricsOnClick,
  anchorClassName,
  onLinkFocus,
}) => {
  const captionClasses =
    'flex items-center justify-center text--xs mb-0 py-2 px-4 text-start no-underline';

  const brandConfig = useBrandConfig();
  const isSurfaceEnabled = brandConfig.surfaceConfig.isEnabled;

  if (!caption) {
    return null;
  }

  return (
    <figcaption
      data-testid="caption-container"
      className={cx('absolute', {
        'end-0 ms-4': align === 'end',
        'start-0 me-4': align === 'start',
        'mb-4 bottom-0': placement === 'bottom',
        'mt-4 top-0': placement === 'top',
      })}
    >
      {captionLink ? (
        <a
          data-e2e="linkedCaption"
          data-testid="img-caption-link"
          className={cx(captionClasses, anchorClassName, 'cursor-pointer', {
            'rounded-l-md': !isSurfaceEnabled && align === 'end',
            'rounded-r-md': !isSurfaceEnabled && align === 'start',
            'bg-bg-inverse text-text-overlay': !isSurfaceEnabled,
            'osc-caption': isSurfaceEnabled,
            'osc-caption--start': isSurfaceEnabled && align === 'start',
          })}
          href={captionLink}
          onClick={(e) => {
            e.stopPropagation();
            metricsOnClick?.();
          }}
          target={captionLinkTarget}
          onFocus={onLinkFocus}
        >
          {captionIcon ? (
            <Icon name={iconMapper[captionIcon]} size="sm" variant="solid" className="me-0.5" />
          ) : null}
          {caption} <Icon name="arrowhead-right" size="sm" />
        </a>
      ) : (
        <div
          data-osc-product="osc-caption"
          className={cx(captionClasses, 'pointer-events-none cursor-default', {
            'rounded-l-md': !isSurfaceEnabled && align === 'end',
            'rounded-r-md': !isSurfaceEnabled && align === 'start',
            'bg-bg-inverse text-text-overlay': !isSurfaceEnabled,
            'osc-caption': isSurfaceEnabled,
            'osc-caption--start': isSurfaceEnabled && align === 'start',
          })}
        >
          {captionIcon ? (
            <Icon name={iconMapper[captionIcon]} variant="solid" size="sm" className="me-0.5" />
          ) : null}
          {caption}
        </div>
      )}
    </figcaption>
  );
};
